import { SlideshowSelectEvent } from '@theme/events';

/**
 * Product highlight carousel: external arrows control a full-width slideshow
 * where each slide contains product details and a lifestyle banner.
 */
class FeaturedProductHighlight extends HTMLElement {
  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');
  #scrollAnimation = 0;
  /**
   * Slide the arrow animation is heading to. `slideshow.current` lags the
   * scroll position (it updates asynchronously and only past the halfway
   * point), so while this is set it wins over `slideshow.current`; otherwise
   * scroll-driven syncs flash the old product panel back.
   */
  #pendingIndex = null;
  #pinTimeout = 0;

  connectedCallback() {
    this.slideshow = this.querySelector('slideshow-component');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.dotsContainer = this.querySelector('[data-carousel-dots]');
    this.carouselControls = this.querySelector('[data-carousel-controls]');
    this.dotsHome = this.dotsContainer?.parentElement ?? null;
    this.productPanels = Array.from(this.querySelectorAll('[data-product-panel]'));

    if (!this.slideshow) return;

    this.scroller = this.slideshow.refs?.scroller ?? this.slideshow.querySelector('slideshow-slides');

    this.#prepareSlideshow(this.slideshow);

    this.prevButton?.addEventListener('click', this.#handlePrev);
    this.nextButton?.addEventListener('click', this.#handleNext);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect);
    this.#mobileMediaQuery.addEventListener('change', this.#syncControls);
    // Cancel an in-flight arrow animation if the user grabs the banner to drag.
    this.scroller?.addEventListener('pointerdown', this.#cancelScrollAnimation);
    window.addEventListener('resize', this.#positionArrows, { passive: true });
    window.addEventListener('resize', this.#syncControls, { passive: true });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncControls();
      });
    });
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.#handlePrev);
    this.nextButton?.removeEventListener('click', this.#handleNext);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect);
    this.#mobileMediaQuery.removeEventListener('change', this.#syncControls);
    this.scroller?.removeEventListener('pointerdown', this.#cancelScrollAnimation);
    window.removeEventListener('resize', this.#positionArrows);
    window.removeEventListener('resize', this.#syncControls);
    this.#cancelScrollAnimation();
  }

  /** @param {MouseEvent} event */
  #handlePrev = (event) => {
    event.preventDefault();
    if (!this.slideshow || this.prevButton?.disabled) return;

    this.#prepareSlideshow(this.slideshow);
    this.#animateToIndex((this.#pendingIndex ?? this.slideshow.current ?? 0) - 1);
  };

  /** @param {MouseEvent} event */
  #handleNext = (event) => {
    event.preventDefault();
    if (!this.slideshow || this.nextButton?.disabled) return;

    this.#prepareSlideshow(this.slideshow);
    this.#animateToIndex((this.#pendingIndex ?? this.slideshow.current ?? 0) + 1);
  };

  /**
   * Slides the banner to a target index by animating the scroller's own
   * scroll position, exactly as a drag/scroll would.
   *
   * The base slideshow's `next()`/`previous()` scroll programmatically, which
   * (a) marks the scroll as non-user so the slideshow's live scroll-sync is
   * skipped, and (b) is short-circuited into an instant jump by
   * `scroll-snap-type: mandatory`. By writing `scrollLeft` per frame instead,
   * the browser fires real scroll events: the slideshow tracks the position,
   * dispatches its `scroll` select event, dots/arrows update, and the product
   * panel crossfades — identical to dragging the banner.
   *
   * @param {number} targetIndex
   */
  #animateToIndex(targetIndex) {
    const slideshow = this.slideshow;
    const scroller = this.scroller;
    const slides = slideshow?.slides;

    if (!(scroller instanceof HTMLElement) || !slides?.length) return;

    const clamped = Math.max(0, Math.min(targetIndex, slides.length - 1));
    const target = slides[clamped];
    if (!(target instanceof HTMLElement)) return;

    const start = scroller.scrollLeft;
    const distance = target.offsetLeft - start;
    if (Math.abs(distance) < 1) return;

    this.#cancelScrollAnimation();

    // Fade to the matching product panel right away, mirroring a drag release.
    this.#pendingIndex = clamped;
    this.#syncActivePanel(clamped);

    // Disable snap for the duration so it can't snap the animation to a jump.
    scroller.style.scrollSnapType = 'none';

    const duration = 450;
    let startTime = null;
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const step = (now) => {
      if (startTime === null) startTime = now;
      const progress = Math.min(1, (now - startTime) / duration);
      scroller.scrollLeft = start + distance * ease(progress);

      if (progress < 1) {
        this.#scrollAnimation = requestAnimationFrame(step);
      } else {
        this.#scrollAnimation = 0;
        scroller.style.scrollSnapType = '';
        // Keep the pin: `slideshow.current` is still stale here. It is released
        // in #syncActivePanel once the slideshow reports the target, or by this
        // fallback timeout.
        this.#pinTimeout = setTimeout(this.#releasePin, 1000);
        this.#syncControls();
      }
    };

    this.#scrollAnimation = requestAnimationFrame(step);
  }

  #cancelScrollAnimation = () => {
    if (this.#scrollAnimation) {
      cancelAnimationFrame(this.#scrollAnimation);
      this.#scrollAnimation = 0;
    }
    this.#releasePin();
    if (this.scroller instanceof HTMLElement) {
      this.scroller.style.scrollSnapType = '';
    }
  };

  #releasePin = () => {
    clearTimeout(this.#pinTimeout);
    this.#pendingIndex = null;
  };

  /** @param {CustomEvent} event */
  #onSlideshowSelect = (event) => {
    if (event.target !== this.slideshow) return;
    this.#syncControls();
  };

  /**
   * Crossfade the product panels: only the panel matching the current banner
   * slide is shown. The banner slides horizontally inside the slideshow while
   * the product panel fades in place.
   *
   * @param {number} [indexOverride] - Force a specific active panel (defaults to the current slide).
   */
  #syncActivePanel = (indexOverride) => {
    const slideshow = this.slideshow;
    if (!slideshow || !this.productPanels?.length) return;

    // The slideshow has caught up with the animation target: hand control back.
    if (!this.#scrollAnimation && this.#pendingIndex !== null && slideshow.current === this.#pendingIndex) {
      this.#releasePin();
    }

    const current = indexOverride ?? this.#pendingIndex ?? slideshow.current ?? 0;

    this.productPanels.forEach((panel, index) => {
      const isActive = index === current;
      panel.toggleAttribute('data-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  };

  #positionArrows = () => {
    const carousel = this.querySelector('.featured-product-highlight__carousel');
    const arrows = this.querySelector('[data-carousel-controls]');
    const slideshow = this.slideshow;

    if (!(carousel instanceof HTMLElement) || !(arrows instanceof HTMLElement) || !slideshow) return;

    const activePanel = this.productPanels?.[this.#pendingIndex ?? slideshow.current] ?? this.productPanels?.[0];
    const mediaRow = activePanel?.querySelector('.featured-product-highlight__media-row');

    if (!(mediaRow instanceof HTMLElement)) return;

    const carouselRect = carousel.getBoundingClientRect();
    const mediaRect = mediaRow.getBoundingClientRect();
    const centerY = mediaRect.top + mediaRect.height / 2 - carouselRect.top;

    arrows.style.top = `${centerY}px`;
  };

  /**
   * @param {HTMLElement} slideshow
   */
  #prepareSlideshow(slideshow) {
    slideshow.setAttribute('in-viewport', '');
  }

  /**
   * @param {HTMLElement} slideshow
   */
  #canScrollSlideshow(slideshow) {
    const scroller = slideshow.refs?.scroller ?? slideshow.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return false;

    return scroller.scrollWidth > scroller.clientWidth + 1;
  }

  #usesMobileDots() {
    return this.#mobileMediaQuery.matches;
  }

  #positionDots() {
    if (!this.dotsContainer) return;

    if (!this.#usesMobileDots()) {
      if (this.dotsHome instanceof HTMLElement && this.dotsContainer.parentElement !== this.dotsHome) {
        this.dotsHome.appendChild(this.dotsContainer);
      }
      this.dotsContainer.hidden = true;
      return;
    }

    const slideshow = this.slideshow;
    const activePanel = this.productPanels?.[this.#pendingIndex ?? slideshow?.current ?? 0];
    const mount = activePanel?.querySelector('[data-dots-mount]');

    if (!(mount instanceof HTMLElement)) return;

    mount.appendChild(this.dotsContainer);
  }

  #syncControls = () => {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    this.#syncActivePanel();

    const slides = slideshow.slides;
    const slideCount = slides?.length ?? 0;
    const canScroll = this.#canScrollSlideshow(slideshow);
    const hasMultipleSlides = canScroll && slideCount > 1;

    if (this.carouselControls) {
      this.carouselControls.hidden = !hasMultipleSlides;
    }

    if (!hasMultipleSlides) {
      if (this.dotsContainer) {
        this.dotsContainer.hidden = true;
        this.dotsContainer.innerHTML = '';
      }
      return;
    }

    if (this.#usesMobileDots()) {
      this.#buildDots();
      this.#positionDots();
      this.#syncDotsState();
    } else if (this.dotsContainer) {
      this.dotsContainer.hidden = true;
      this.dotsContainer.innerHTML = '';
      if (this.dotsHome instanceof HTMLElement && this.dotsContainer.parentElement !== this.dotsHome) {
        this.dotsHome.appendChild(this.dotsContainer);
      }
    }

    this.#positionArrows();
    this.#syncArrowState();
  };

  #buildDots() {
    const slideshow = this.slideshow;
    if (!this.dotsContainer || !slideshow) return;

    const slideCount = slideshow.slides?.length ?? 0;

    if (slideCount <= 1) {
      this.dotsContainer.hidden = true;
      this.dotsContainer.innerHTML = '';
      return;
    }

    if (this.dotsContainer.querySelectorAll('.featured-product-highlight__dot').length === slideCount) {
      this.dotsContainer.hidden = false;
      return;
    }

    this.dotsContainer.innerHTML = '';

    const list = document.createElement('div');
    list.className = 'featured-product-highlight__dots-list';
    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-label', 'Product highlight carousel pagination');

    for (let index = 0; index < slideCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'featured-product-highlight__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.setAttribute('aria-selected', index === slideshow.current ? 'true' : 'false');
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.#prepareSlideshow(slideshow);
        slideshow.select(index, event);
      });
      list.appendChild(dot);
    }

    this.dotsContainer.appendChild(list);
    this.dotsContainer.hidden = false;
  }

  #syncDotsState() {
    if (!this.dotsContainer || this.dotsContainer.hidden) return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    this.dotsContainer.querySelectorAll('.featured-product-highlight__dot').forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === slideshow.current ? 'true' : 'false');
    });
  };

  #syncArrowState = () => {
    const slideshow = this.slideshow;
    if (!slideshow || !this.prevButton || !this.nextButton) return;

    const slides = slideshow.slides;
    const slideCount = slides?.length ?? 0;
    const canScroll = this.#canScrollSlideshow(slideshow);

    if (!canScroll || slideCount <= 1) {
      this.#setArrowDisabled(this.prevButton, true);
      this.#setArrowDisabled(this.nextButton, true);
      return;
    }

    const atStart = slideshow.current <= 0;
    const atEnd = slideshow.nextIndex >= slideCount;

    this.#setArrowDisabled(this.prevButton, atStart);
    this.#setArrowDisabled(this.nextButton, atEnd);
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} disabled
   */
  #setArrowDisabled(button, disabled) {
    button.disabled = disabled;
    button.classList.toggle('featured-product-highlight__arrow--disabled', disabled);
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }
}

if (!customElements.get('featured-product-highlight')) {
  customElements.define('featured-product-highlight', FeaturedProductHighlight);
}
